import React, { useState, useEffect } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import {
  effectivenessList,
  systemInformAreaList,
  systemInformKPIList,
} from "../../../../utils/types";
import { formatOptions } from "../../../../utils/helpers";

interface ExternalDataSharingEditProps {
  control: any;
  errors: any;
  watch: any;
  setValue: any;
}

const ExternalDataSharingEdit: React.FC<ExternalDataSharingEditProps> = ({
  control,
  errors,
  watch,
  setValue,
}) => {
  const [customKPI, setCustomKPI] = useState<string>("");
  const [customKPIs, setCustomKPIs] = useState<string[]>([]);
  const [hasOtherKPI, setHasOtherKPI] = useState<boolean>(false);

  const systemInformKPIOptions = [
    ...formatOptions(systemInformKPIList),
    { value: "Others", label: "Others" },
  ];

  const systemInformAreaOptions = formatOptions(systemInformAreaList);
  const effectivenessOptions = formatOptions(effectivenessList);

  // Watch if "Others" is selected
  const selectedKPIs = watch("systemInformKPI") || [];

  useEffect(() => {
    // Check if "Others" is selected
    const othersSelected = selectedKPIs.includes("Others");
    setHasOtherKPI(othersSelected);

    // Extract custom KPIs from selected values (excluding "Others" and predefined options)
    const predefinedValues = systemInformKPIList.map(
      (item: string | { value: string }) =>
        typeof item === "string" ? item : item.value
    );
    const customValues = selectedKPIs.filter(
      (kpi) => kpi !== "Others" && !predefinedValues.includes(kpi)
    );
    setCustomKPIs(customValues);
  }, [selectedKPIs]);

  const handleKPIChange = (selected: any) => {
    const values = selected ? selected.map((opt: any) => opt.value) : [];
    setValue("systemInformKPI", values);
  };

  const addCustomKPI = () => {
    if (customKPI.trim() && !customKPIs.includes(customKPI.trim())) {
      const newCustomKPIs = [...customKPIs, customKPI.trim()];
      setCustomKPIs(newCustomKPIs);

      // Update form value with custom KPIs (excluding "Others" if no custom KPIs)
      const predefinedSelected = selectedKPIs.filter(
        (kpi) => kpi !== "Others" && systemInformKPIList.includes(kpi as any)
      );

      const newValues = [...predefinedSelected, ...newCustomKPIs];
      if (newCustomKPIs.length > 0 && !selectedKPIs.includes("Others")) {
        newValues.push("Others");
      }

      setValue("systemInformKPI", newValues);
      setCustomKPI("");
    }
  };

  const removeCustomKPI = (kpiToRemove: string) => {
    const newCustomKPIs = customKPIs.filter((kpi) => kpi !== kpiToRemove);
    setCustomKPIs(newCustomKPIs);

    // Update form value
    const predefinedSelected = selectedKPIs.filter(
      (kpi) => kpi !== "Others" && systemInformKPIList.includes(kpi as any)
    );

    const newValues = [...predefinedSelected, ...newCustomKPIs];
    if (newCustomKPIs.length > 0) {
      newValues.push("Others");
    }

    setValue("systemInformKPI", newValues);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addCustomKPI();
    }
  };

  // Create options for Select including custom KPIs
  const allKPIOptions = [
    ...systemInformKPIOptions,
    ...customKPIs.map((kpi) => ({ value: kpi, label: kpi })),
  ];

  return (
    <div className="font-Inter max-w-2xl">
      <div className="space-y-6">
        {/* KPI Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which KPI does this system inform?{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemInformKPI"
            control={control}
            render={({ field }) => (
              <div>
                <Select
                  isMulti
                  options={allKPIOptions}
                  placeholder="Select KPI(s)..."
                  onChange={handleKPIChange}
                  value={allKPIOptions.filter((opt) =>
                    field.value?.includes(opt.value)
                  )}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />

                {/* Custom KPI Input */}
                {hasOtherKPI && (
                  <div className="mt-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customKPI}
                        onChange={(e) => setCustomKPI(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter a custom KPI"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={addCustomKPI}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        Add
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Press Enter or click Add to include a custom KPI
                    </p>

                    {/* Display custom KPIs */}
                    {customKPIs.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Custom KPIs:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {customKPIs.map((kpi) => (
                            <span
                              key={kpi}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {kpi}
                              <button
                                type="button"
                                onClick={() => removeCustomKPI(kpi)}
                                className="ml-1.5 rounded-full flex-shrink-0"
                              >
                                <span className="sr-only">Remove</span>
                                <svg
                                  className="h-2 w-2"
                                  stroke="currentColor"
                                  fill="none"
                                  viewBox="0 0 8 8"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeWidth="1.5"
                                    d="M1 1l6 6m0-6L1 7"
                                  />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          />
          {errors.systemInformKPI && (
            <p className="text-red-500 text-xs mt-1">
              {errors.systemInformKPI.message}
            </p>
          )}
        </div>

        {/* Area Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which Area does this system inform?{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemInformArea"
            control={control}
            render={({ field }) => (
              <Select
                isMulti
                options={systemInformAreaOptions}
                placeholder="Select Area(s)..."
                onChange={(selected) =>
                  field.onChange(
                    selected ? selected.map((opt: any) => opt.value) : []
                  )
                }
                value={systemInformAreaOptions.filter((opt) =>
                  field.value?.includes(opt.value)
                )}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            )}
          />
          {errors.systemInformArea && (
            <p className="text-red-500 text-xs mt-1">
              {errors.systemInformArea.message}
            </p>
          )}
        </div>

        {/* External Systems */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Which external department and system receives data from this system{" "}
            <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemReceiveData"
            control={control}
            render={({ field }) => (
              <textarea
                {...field}
                placeholder="Enter the external system e.g. MWDS Digital Atlas"
                className={`resize-none border w-full p-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.systemReceiveData
                    ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                    : "border-gray-300 focus:border-blue-500"
                }`}
                rows={3}
              />
            )}
          />
          {errors.systemReceiveData && (
            <p className="text-red-500 text-xs mt-1">
              {errors.systemReceiveData.message}
            </p>
          )}
        </div>

        {/* Communication Effectiveness */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            How effective is the communication between this system and the one
            that feeds it information? <span className="text-red-500">*</span>
          </label>
          <Controller
            name="systemCommunicationEffectiveness"
            control={control}
            render={({ field }) => (
              <Select
                options={effectivenessOptions}
                placeholder="Select Effectiveness..."
                onChange={(selected) => field.onChange(selected?.value || "")}
                value={
                  effectivenessOptions.find(
                    (opt) => opt.value === field.value
                  ) || null
                }
                className="react-select-container"
                classNamePrefix="react-select"
              />
            )}
          />
          {errors.systemCommunicationEffectiveness && (
            <p className="text-red-500 text-xs mt-1">
              {errors.systemCommunicationEffectiveness.message}
            </p>
          )}
        </div>
      </div>

      <style>{`
        .react-select-container :global(.react-select__control) {
          border-radius: 0.375rem;
          min-height: 42px;
          border-color: #d1d5db;
        }
        .react-select-container :global(.react-select__control--is-focused) {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }
        .react-select-container
          :global(.react-select__control--is-focused:hover) {
          border-color: #3b82f6;
        }
      `}</style>
    </div>
  );
};

export default ExternalDataSharingEdit;
